import React, {useEffect} from 'react';
import moment, {Moment} from 'moment';
import {useIntl} from 'react-intl';
import {ButtonDropdownField, DateRangePicker, DropdownList, FlexRow, Icon} from '@forecast-it/design-system';
import {
	calculatePresetDateRange,
	PRESET_DATE_RANGE_PICKER_OPTIONS,
} from '../../../forecast-app/shared/components/date-picker/preset_date_range_picker';

/*
 * The All invoices date range in the design system, as on the Expense management page: a preset
 * dropdown and a date range field. Same presets and the same page callbacks as the header's preset
 * picker and date range picker.
 */

export type PresetDateRange = {option: string; startDate: Moment | null; endDate: Moment | null};

const PRESETS: [string, string][] = [
	[PRESET_DATE_RANGE_PICKER_OPTIONS.ALL_TIME, 'common.all_time'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.PREVIOUS_MONTH, 'common.previous_month'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.CURRENT_MONTH, 'common.current_month'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.NEXT_MONTH, 'date_span_picker.next_month'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.PREVIOUS_YEAR, 'common.previous_year'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.CURRENT_YEAR, 'common.current_year'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.YEAR_TO_DATE, 'common.year_to_date'],
	[PRESET_DATE_RANGE_PICKER_OPTIONS.CUSTOM_RANGE, 'common.custom_range'],
];

type Props = {
	option: string;
	startDate: Moment;
	endDate: Moment;
	showDates: boolean;
	onPresetChange: (range: PresetDateRange) => void;
	onRangeChange: (range: {startDate: Moment; endDate: Moment}) => void;
};

export const InvoicesDateRangeControls = ({
	option,
	startDate,
	endDate,
	showDates,
	onPresetChange,
	onRangeChange,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();

	// As the header's preset picker: a relative preset (Current month, ...) is worked out again on open.
	useEffect(() => {
		if (option) {
			onPresetChange(calculatePresetDateRange(option));
		}
	}, []);

	const selected = PRESETS.find(([value]) => value === option) ?? PRESETS[PRESETS.length - 1];

	return (
		<FlexRow gap={'s'}>
			{/* Its own box: the dropdown list's container would otherwise stretch across half the row. */}
			<div>
				<DropdownList>
					<DropdownList.Trigger>
						<ButtonDropdownField width="180px" data-cy={'invoices-date-range-preset'}>
							{formatMessage({id: selected[1]})}
						</ButtonDropdownField>
					</DropdownList.Trigger>
					<DropdownList.Content offset={10} align={'start'} width="199px">
						{PRESETS.map(([value, messageId]) => (
							<DropdownList.Item
								key={value}
								onClick={() => onPresetChange(calculatePresetDateRange(value))}
								data-cy={'invoices-date-range-preset-' + value}
							>
								<FlexRow justifyContent={'space-between'}>
									<div>{formatMessage({id: messageId})}</div>
									{value === selected[0] && <Icon icon="tick" />}
								</FlexRow>
							</DropdownList.Item>
						))}
					</DropdownList.Content>
				</DropdownList>
			</div>
			{showDates && (
				<DateRangePicker
					startDate={startDate.format('YYYY-MM-DD')}
					endDate={endDate.format('YYYY-MM-DD')}
					onChange={(range: {startDate: string | null; endDate: string | null}) => {
						// Both ends are needed: the page reloads the list for a full range only.
						if (range.startDate && range.endDate) {
							onRangeChange({startDate: moment(range.startDate), endDate: moment(range.endDate)});
						}
					}}
					aria-label={formatMessage({id: 'common.dates'})}
					data-cy={'invoices-date-range-picker'}
				/>
			)}
		</FlexRow>
	);
};
